import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { useRouter } from 'next/router'

import { siteConfig } from '@/constants/site'

export const Seo: FC<SeoProps> = ({
    title,
    fullTitle,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage,
    canonical,
    noIndex
}): ReactNode => {
    const router = useRouter()

    const pageTitle = fullTitle
        ? fullTitle
        : title
          ? `${title} | ${siteConfig.name}`
          : siteConfig.name

    const image = ogImage
        ? ogImage.startsWith('http')
            ? ogImage
            : `${siteConfig.url}${ogImage}`
        : undefined

    // Every page gets its own canonical, derived from its real path, so pages
    // stop declaring themselves the homepage.
    const path = (router.asPath || '/').split('?')[0].split('#')[0]
    const canonicalUrl = canonical || `${siteConfig.url}${path === '/' ? '' : path}`

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta name='viewport' content='width=device-width, initial-scale=1' />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:url' content={canonicalUrl} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {image && <meta property='og:image' content={image} />}
            {image && <meta property='og:image:alt' content={pageTitle} />}
            {image && <meta name='twitter:image' content={image} />}

            {noIndex
                ? <meta name='robots' content='noindex, nofollow' />
                : <link rel='canonical' href={canonicalUrl} />}
        </Head>
    )
}
